<template>
  <div
    class="popup-holder popup-advanced-export"
    :style="{ display: showAdvancedExportPopup ? 'block' : 'none' }"
  >
    <div
      ref="dialog"
      class="popup-wrap advanced-export"
      role="dialog"
      aria-modal="true"
      aria-label="Export"
      tabindex="-1"
    >
      <div class="popup-head">
        <h2>Export</h2>
        <a
          @click.prevent="close"
          href="#"
          class="popup-close icon-close"
          aria-label="Close"
        ></a>
      </div>

      <div class="popup-body advanced-export__body">
        <!-- Left: live preview -->
        <div class="advanced-export__preview">
          <ExportPreview
            :text="previewText"
            :truncated="previewTruncated"
            :loading="previewLoading"
            :empty-message="previewError || 'Nothing to preview yet.'"
          />
        </div>

        <!-- Right: options -->
        <div class="advanced-export__options">
          <!-- Transcript section -->
          <section class="ae-section">
            <header class="ae-section__head">
              <span class="ae-section__title">Transcript</span>
              <label class="ae-switch">
                <input type="checkbox" v-model="options.transcript.include" />
                <span class="ae-switch__slider"></span>
              </label>
            </header>

            <div v-if="options.transcript.include" class="ae-section__body">
              <label class="ae-field">
                <span>File format</span>
                <select
                  v-model="options.format"
                  class="ae-select"
                  :disabled="loading"
                >
                  <option value="clipboard">Clipboard</option>
                  <option value="txt">TXT</option>
                  <option value="docx">DOCX</option>
                  <option value="pdf">PDF</option>
                  <option value="srt">SRT</option>
                  <option value="vtt">VTT</option>
                </select>
              </label>

              <button
                class="ae-more"
                @click="moreOptionsOpen = !moreOptionsOpen"
              >
                {{ moreOptionsOpen ? "▾" : "▸" }} More options
              </button>

              <div v-if="moreOptionsOpen" class="ae-more__body">
                <label class="ae-check">
                  <input
                    type="checkbox"
                    v-model="options.transcript.showSpeakerNames"
                  />
                  Show speaker names
                </label>
                <label
                  class="ae-check"
                  :class="{ 'ae-check--disabled': proseOptionsDisabled }"
                >
                  <input
                    type="checkbox"
                    v-model="options.transcript.showTimestamps"
                    :disabled="proseOptionsDisabled"
                  />
                  Show timestamps
                </label>
                <label
                  class="ae-check"
                  :class="{ 'ae-check--disabled': proseOptionsDisabled }"
                >
                  <input
                    type="checkbox"
                    v-model="options.transcript.combineSpeakerParagraphs"
                    :disabled="proseOptionsDisabled"
                  />
                  Combine paragraphs of the same speaker
                </label>
                <label
                  class="ae-check"
                  :class="{ 'ae-check--disabled': proseOptionsDisabled }"
                >
                  <input
                    type="checkbox"
                    v-model="options.transcript.combineAllParagraphs"
                    :disabled="proseOptionsDisabled"
                  />
                  Combine all paragraphs
                </label>
                <label
                  class="ae-check"
                  :class="{ 'ae-check--disabled': proseOptionsDisabled }"
                >
                  <input
                    type="checkbox"
                    v-model="options.transcript.removeBranding"
                    :disabled="proseOptionsDisabled"
                  />
                  Remove AudioScribe branding
                </label>
                <p v-if="proseOptionsDisabled" class="ae-note">
                  {{ options.format.toUpperCase() }} is a subtitle format — only
                  the speaker-name option applies.
                </p>
              </div>
            </div>
          </section>

          <!-- Summary section -->
          <section class="ae-section">
            <header class="ae-section__head">
              <span class="ae-section__title">Summary</span>
              <label class="ae-switch">
                <input type="checkbox" v-model="options.includeSummary" />
                <span class="ae-switch__slider"></span>
              </label>
            </header>
            <p v-if="options.includeSummary" class="ae-note">
              The AI summary downloads as a separate .txt file.
            </p>
          </section>

          <!-- Chat History section -->
          <section class="ae-section">
            <header class="ae-section__head">
              <span class="ae-section__title">Chat History</span>
              <label class="ae-switch">
                <input type="checkbox" v-model="options.includeChat" />
                <span class="ae-switch__slider"></span>
              </label>
            </header>
            <p v-if="options.includeChat" class="ae-note">
              Your AI chat for this file downloads as a separate .txt file.
            </p>
          </section>

          <!-- Media section -->
          <section class="ae-section">
            <header class="ae-section__head">
              <span class="ae-section__title">Media</span>
              <label class="ae-switch">
                <input type="checkbox" v-model="options.includeMedia" />
                <span class="ae-switch__slider"></span>
              </label>
            </header>
            <p v-if="options.includeMedia" class="ae-note">
              The {{ isVideo ? "video" : "audio" }} file downloads separately.
            </p>
          </section>
        </div>
      </div>

      <div class="popup-foot advanced-export__foot">
        <label class="ae-filename">
          Filename:
          <input
            v-model="filename"
            class="ae-filename__input"
            type="text"
            spellcheck="false"
          />
        </label>
        <div class="ae-actions">
          <button
            type="button"
            class="ae-reset"
            :disabled="loading"
            @click="resetExportPreferences"
            title="Restore the default export settings"
          >
            Reset to defaults
          </button>
          <button class="btn btn-secondary" @click="close">Cancel</button>
          <button
            class="btn btn-primary"
            :disabled="loading || !canExport"
            @click="handleExport"
          >
            <span v-if="loading" class="spinner"></span>
            <span v-else>Export</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapStores } from "pinia";
import { useAppStore } from "@/stores/app-store";
import { useWorkSpaceStore } from "@/stores/workspace-store";
import { useTranscriptEditingStore } from "@/stores/transcript-editing-store";
import {
  exportTranscript,
  exportArchive,
  downloadAudioFile,
  downloadSummary,
  downloadChatHistory,
} from "@/services/workspace.service";
import { downloadFile } from "@/utils/downloadHelpers";
import { renderTranscriptText } from "@/utils/transcriptExportFormat";
import {
  defaultExportOptions,
  loadExportPreferences,
  saveExportPreferences,
  clearExportPreferences,
} from "@/utils/exportPreferences";
import { analytics } from "@/services/analytics.service";
import ExportPreview from "./export/ExportPreview.vue";

const MAX_PREVIEW_WORDS = 5000;
// Kept in sync with the backend PREVIEW_SUBTITLE_WORDS cap.
const PREVIEW_SUBTITLE_WORDS = 1000;
const SCHEMA_VERSION = 1;

export default {
  name: "AdvancedExportModal",
  components: { ExportPreview },
  props: ["showAdvancedExportPopup"],
  computed: {
    ...mapStores(useAppStore),
    ...mapStores(useWorkSpaceStore),
    ...mapStores(useTranscriptEditingStore),
    audioFile() {
      return this.workspaceStore.activeAudioFile;
    },
    words() {
      return this.audioFile?.transcriptJson?.words || [];
    },
    isVideo() {
      return this.audioFile?.fileType === "video";
    },
    isSubtitleFormat() {
      return this.options.format === "srt" || this.options.format === "vtt";
    },
    proseOptionsDisabled() {
      // Subtitle formats (SRT/VTT) honor only the speaker-name option.
      return this.isSubtitleFormat;
    },
    previewTruncated() {
      // Both previews are capped: subtitle previews to the backend's sample
      // size, text projections to MAX_PREVIEW_WORDS.
      const cap = this.isSubtitleFormat
        ? PREVIEW_SUBTITLE_WORDS
        : MAX_PREVIEW_WORDS;
      return this.words.length > cap;
    },
    previewText() {
      if (!this.options.transcript.include) return "";
      // SRT/VTT: show the real subtitle output fetched from the backend (the
      // same generator the download uses) — see fetchSubtitlePreview().
      if (this.isSubtitleFormat) return this.subtitlePreview;
      // Text formats (clipboard/txt/docx/pdf): instant client-side projection.
      const previewWords = this.previewTruncated
        ? this.words.slice(0, MAX_PREVIEW_WORDS)
        : this.words;
      return renderTranscriptText(previewWords, { ...this.options.transcript });
    },
    canExport() {
      return (
        this.options.transcript.include ||
        this.options.includeSummary ||
        this.options.includeChat ||
        this.options.includeMedia
      );
    },
    /**
     * The file being exported comes from the transcript route. Null when the
     * modal is open off a transcript page (e.g. left open across a
     * navigation) — export and preview must never fire with a literal
     * "undefined" id, which the API answers with a 404.
     */
    routeAudioFileUUID() {
      return this.$route?.params?.audioFileUUID || null;
    },
  },
  data() {
    return {
      loading: false,
      moreOptionsOpen: false,
      filename: "",
      options: this.defaultOptions(),
      // While true, programmatic option changes (rehydrate on open, reset) do NOT
      // write to storage — only genuine user edits persist. Reset back on $nextTick,
      // after Vue's watcher flush, so the suppressed change has already been seen.
      suppressPersist: false,
      // Backend-fetched preview for subtitle formats (SRT/VTT).
      subtitlePreview: "",
      previewLoading: false,
      previewError: "",
      previewDebounceTimer: null,
      previewRequestSeq: 0,
      previewAbort: null,
      previouslyFocused: null,
      // Set when "opened" is tracked: "closed" reports the same file, once.
      openSession: null,
    };
  },
  watch: {
    showAdvancedExportPopup(open) {
      if (open) {
        // Remember what to restore focus to, then move focus into the dialog.
        this.previouslyFocused = document.activeElement;
        this.resetForActiveFile();
        this.$nextTick(() => this.focusDialog());
      } else {
        this.restoreFocus();
      }
    },
    "options.format"() {
      if (this.isSubtitleFormat) {
        this.schedulePreviewRefresh();
      } else {
        this.clearSubtitlePreview();
      }
    },
    "options.transcript.showSpeakerNames"() {
      // The only transcript option subtitle output reflects.
      if (this.isSubtitleFormat) this.schedulePreviewRefresh();
    },
    "options.transcript.include"(include) {
      if (this.isSubtitleFormat && include) this.schedulePreviewRefresh();
    },
    // Persist the user's export configuration live, so switching files or refreshing
    // keeps their last-used choices. Programmatic changes are suppressed (see
    // suppressPersist) so opening the modal / resetting never silently rewrites storage.
    options: {
      handler() {
        this.persistPreferences();
      },
      deep: true,
    },
    moreOptionsOpen() {
      this.persistPreferences();
    },
    // Navigating to another file (or away) while Export is open would leave
    // it pointed at the wrong transcript — close it instead.
    "$route.params.audioFileUUID"(newId, oldId) {
      if (this.showAdvancedExportPopup && newId !== oldId) this.close();
    },
  },
  mounted() {
    document.addEventListener("keydown", this.onKeydown);
    // A freshly mounted page never starts with Export open. The open flag
    // lives in the app store, so a navigation can hand this new instance a
    // stale `true` without firing the open watcher that sets the dialog up.
    if (this.showAdvancedExportPopup) this.close();
  },
  beforeUnmount() {
    clearTimeout(this.previewDebounceTimer);
    if (this.previewAbort) this.previewAbort.abort();
    document.removeEventListener("keydown", this.onKeydown);
    // Leaving for another page unmounts the modal (it lives in each page's
    // layout): finish the open session here, and clear the open flag so the
    // next page doesn't inherit it.
    if (this.openSession) this.close();
  },
  methods: {
    // Defaults live in the persistence util so the modal and the storage layer can
    // never drift on what "default" means.
    defaultOptions() {
      return defaultExportOptions();
    },
    resetForActiveFile() {
      // Rehydrate the user's last-used export configuration (everything except the
      // per-file filename). Suppress persistence so simply opening the modal doesn't
      // rewrite storage. If nothing is stored, loadExportPreferences() returns defaults.
      const { options, moreOptionsOpen } = loadExportPreferences();
      this.applyWithoutPersist(() => {
        this.options = options;
        this.moreOptionsOpen = moreOptionsOpen;
      });
      this.filename = this.defaultFilename();
      this.clearSubtitlePreview();
      // A rehydrated subtitle format (srt/vtt) may equal the previous open's value,
      // so the value-change preview watchers won't fire — schedule the backend
      // preview explicitly. schedulePreviewRefresh() self-guards to subtitle formats
      // with the transcript included, so this is a no-op for text formats.
      this.schedulePreviewRefresh();
      this.openSession = { audioFileUUID: this.routeAudioFileUUID };
      this.trackQuietly("advancedExportOpened", {
        audio_file_uuid: this.openSession.audioFileUUID,
      });
    },
    // Best-effort live persist of the current export configuration.
    persistPreferences() {
      if (this.suppressPersist) return;
      saveExportPreferences({
        options: this.options,
        moreOptionsOpen: this.moreOptionsOpen,
      });
    },
    // Apply a programmatic options change without persisting it. The suppress flag is
    // cleared on $nextTick — after Vue's pre-flush watchers (including the deep
    // `options` watcher) have already run against the change.
    applyWithoutPersist(mutate) {
      this.suppressPersist = true;
      try {
        mutate();
      } finally {
        // Always re-enable persistence — even if mutate() throws — so a stray error
        // can never silently disable preference saving for the rest of the session.
        this.$nextTick(() => {
          this.suppressPersist = false;
        });
      }
    },
    // "Reset to defaults": clear stored prefs and restore the canonical defaults. We
    // suppress the watcher write so reset genuinely clears storage rather than
    // immediately recreating the key with defaults.
    resetExportPreferences() {
      clearExportPreferences();
      this.applyWithoutPersist(() => {
        this.options = defaultExportOptions();
        this.moreOptionsOpen = false;
      });
    },
    clearSubtitlePreview() {
      // Bumping the sequence invalidates any scheduled or in-flight fetch so a
      // late response can't write stale state after we've moved on / closed.
      this.previewRequestSeq++;
      clearTimeout(this.previewDebounceTimer);
      if (this.previewAbort) {
        this.previewAbort.abort();
        this.previewAbort = null;
      }
      this.subtitlePreview = "";
      this.previewError = "";
      this.previewLoading = false;
    },
    /** Debounced refresh of the backend-generated SRT/VTT preview. */
    schedulePreviewRefresh() {
      if (!this.isSubtitleFormat || !this.options.transcript.include) return;
      // Invalidate + abort any earlier in-flight request immediately so it
      // can't publish stale preview text (or clear loading) during this new
      // debounce window.
      this.previewRequestSeq++;
      if (this.previewAbort) {
        this.previewAbort.abort();
        this.previewAbort = null;
      }
      clearTimeout(this.previewDebounceTimer);
      this.previewError = "";
      this.previewLoading = true;
      this.previewDebounceTimer = setTimeout(
        () => this.fetchSubtitlePreview(),
        250,
      );
    },
    /**
     * Fetch the real subtitle output from the same export endpoint the download
     * uses, so the preview is format-accurate with zero formatter duplication.
     * Guards against out-of-order responses via a request sequence number.
     */
    async fetchSubtitlePreview(retriesLeft = 1) {
      if (!this.isSubtitleFormat || !this.options.transcript.include) return;
      const requestId = ++this.previewRequestSeq;
      const audioFileUUID = this.routeAudioFileUUID;
      const workspaceUUID = this.workspaceStore.activeWorkspaceUUID;
      if (!audioFileUUID) {
        // Off a transcript page (see routeAudioFileUUID): nothing to preview.
        this.previewLoading = false;
        return;
      }

      // Cancel any prior in-flight preview so we don't pile up backend work.
      if (this.previewAbort) this.previewAbort.abort();
      const controller = new AbortController();
      this.previewAbort = controller;

      try {
        const { data, error } = await exportTranscript(
          workspaceUUID,
          audioFileUUID,
          this.buildRequestBody(true),
          controller.signal,
        );
        if (requestId !== this.previewRequestSeq) return; // superseded

        if (error) {
          if (error.status === 409 && retriesLeft > 0) {
            // The preview's revision is stale — refresh the active file (which
            // bumps transcriptVersion) and retry ONCE with the new revision.
            // Store signature is (audioFileUUID, workspaceUUID, force) — these
            // were previously passed swapped, so the refresh never landed.
            await this.workspaceStore.fetchAudioFile(
              audioFileUUID,
              workspaceUUID,
              true,
            );
            if (requestId !== this.previewRequestSeq) return;
            this.previewLoading = true;
            return await this.fetchSubtitlePreview(retriesLeft - 1);
          }
          this.subtitlePreview = "";
          this.previewError =
            error.status === 409
              ? "Transcript keeps changing — close and reopen to preview."
              : "Subtitle preview is unavailable for this file.";
          return;
        }

        const text = await data.text();
        if (requestId !== this.previewRequestSeq) return; // superseded during blob read
        this.subtitlePreview = text;
      } catch (e) {
        if (requestId !== this.previewRequestSeq) return;
        this.subtitlePreview = "";
        this.previewError = "Couldn't load the subtitle preview.";
      } finally {
        if (requestId === this.previewRequestSeq) this.previewLoading = false;
      }
    },
    defaultFilename() {
      const name = this.audioFile?.userDefinedName || "transcript";
      return name.replace(/\.[^/.]+$/, "");
    },
    close() {
      // Cancel any scheduled/in-flight subtitle preview so closing during the
      // debounce window doesn't fire a pointless backend export.
      this.clearSubtitlePreview();
      // Report the file this open was for — after a navigation the route
      // already names the next one. No session (a stale flag cleared on
      // mount) means no "opened" went out, so no "closed" either.
      if (this.openSession) {
        this.trackQuietly("advancedExportClosed", {
          audio_file_uuid: this.openSession.audioFileUUID,
        });
        this.openSession = null;
      }
      this.appStore.showAdvancedExportPopup = false;
    },
    onKeydown(event) {
      if (!this.showAdvancedExportPopup) return;
      if (event.key === "Escape" && !this.loading) {
        this.close();
        return;
      }
      if (event.key === "Tab") this.trapFocus(event);
    },
    focusableEls() {
      const dialog = this.$refs.dialog;
      if (!dialog) return [];
      return Array.from(
        dialog.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null);
    },
    focusDialog() {
      const els = this.focusableEls();
      (els[0] || this.$refs.dialog)?.focus();
    },
    restoreFocus() {
      if (this.previouslyFocused && this.previouslyFocused.focus) {
        this.previouslyFocused.focus();
      }
      this.previouslyFocused = null;
    },
    // Keep Tab focus cycling inside the modal.
    trapFocus(event) {
      const els = this.focusableEls();
      if (els.length === 0) return;
      const first = els[0];
      const last = els[els.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    buildRequestBody(preview = false) {
      return {
        schemaVersion: SCHEMA_VERSION,
        format: this.options.format,
        filename: (this.filename || "transcript").trim(),
        transcript: {
          include: this.options.transcript.include,
          showSpeakerNames: this.options.transcript.showSpeakerNames,
          showTimestamps: this.options.transcript.showTimestamps,
          combineSpeakerParagraphs:
            this.options.transcript.combineSpeakerParagraphs,
          combineAllParagraphs: this.options.transcript.combineAllParagraphs,
          removeBranding: this.options.transcript.removeBranding,
        },
        includeMedia: this.options.includeMedia,
        expectedTranscriptRevision: this.audioFile?.transcriptVersion || 0,
        preview,
      };
    },
    /** Wait for any in-flight transcript auto-save so we export the latest edits. */
    async waitForPendingSave() {
      const editing = this.transcriptEditingStore;
      const deadline = Date.now() + 5000;
      while (editing.isSaving && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      // If a save is still in flight (or failed), refuse to export rather than
      // ship a transcript that doesn't include the latest edit.
      if (editing.isSaving || editing.lastSaveError) {
        throw new Error(
          "Your latest transcript edit is still saving. Please try again in a moment.",
        );
      }
    },
    async handleExport() {
      if (!this.canExport) return;
      const audioFileUUID = this.routeAudioFileUUID;
      if (!audioFileUUID) {
        // Off a transcript page (see routeAudioFileUUID): nothing to export.
        this.$toast.error("Open a transcript to export it.");
        this.close();
        return;
      }
      this.loading = true;
      const workspaceUUID = this.workspaceStore.activeWorkspaceUUID;

      try {
        await this.waitForPendingSave();

        // Build the request AFTER the flush so expectedTranscriptRevision and
        // the options reflect the latest saved state (and a save completing
        // during the wait doesn't trigger a spurious 409). Snapshotting here
        // also means changing the form after this point can't corrupt response
        // handling.
        const requestBody = this.buildRequestBody();
        // Snapshot the secondary toggles too so changing the form mid-export
        // can't alter what we download.
        const includeSummary = this.options.includeSummary;
        const includeChat = this.options.includeChat;
        const includeMedia = this.options.includeMedia;
        // Summary, chat and media are best-effort: each reports its own
        // failure and returns false instead of throwing, so the rest of the
        // export still downloads.
        const failedParts = [];

        // A clipboard transcript is always a client-side copy — never a file and
        // never bundled into a zip.
        const isClipboard = requestBody.format === "clipboard";
        const transcriptAsFile = requestBody.transcript.include && !isClipboard;
        if (requestBody.transcript.include && isClipboard) {
          await this.copyTranscriptToClipboard();
        }

        // When 2+ TEXT artifacts download as files, bundle them into one .zip
        // instead of firing multiple separate downloads. (Media is never
        // bundled — it downloads straight from GCS.)
        const fileArtifactCount =
          (transcriptAsFile ? 1 : 0) +
          (includeSummary ? 1 : 0) +
          (includeChat ? 1 : 0);

        if (fileArtifactCount >= 2) {
          await this.exportArchiveBundle(workspaceUUID, audioFileUUID, {
            requestBody,
            includeTranscript: transcriptAsFile,
            includeSummary,
            includeChat,
          });
        } else {
          if (transcriptAsFile) {
            await this.exportTranscriptFile(
              workspaceUUID,
              audioFileUUID,
              requestBody,
            );
          }
          if (
            includeSummary &&
            !(await this.exportSummary(workspaceUUID, audioFileUUID))
          ) {
            failedParts.push("summary");
          }
          if (
            includeChat &&
            !(await this.exportChatHistory(workspaceUUID, audioFileUUID))
          ) {
            failedParts.push("chat");
          }
        }

        if (
          includeMedia &&
          !(await this.exportMedia(workspaceUUID, audioFileUUID))
        ) {
          failedParts.push("media");
        }

        const outcome = {
          audio_file_uuid: audioFileUUID,
          format: requestBody.format,
          include_transcript: requestBody.transcript.include,
          include_summary: includeSummary,
          include_chat: includeChat,
          include_media: includeMedia,
          bundled: fileArtifactCount >= 2,
          failed_parts: failedParts,
        };
        const requestedParts = [
          requestBody.transcript.include,
          includeSummary,
          includeChat,
          includeMedia,
        ].filter(Boolean).length;
        if (failedParts.length > 0 && failedParts.length === requestedParts) {
          // Nothing was saved; each part already told the user why.
          this.trackQuietly("advancedExportFailed", {
            ...outcome,
            reason: "all_parts_failed",
          });
          return;
        }
        this.trackQuietly("advancedExportCompleted", outcome);
      } catch (error) {
        // A stale-transcript conflict already refreshed + messaged the user;
        // don't double-toast or mark the export as failed/completed.
        if (error?.isStale) return;
        console.error("Advanced export failed:", error);
        this.$toast.error(error.message || "Failed to export.");
        this.trackQuietly("advancedExportFailed", {
          audio_file_uuid: audioFileUUID,
          reason: String(error?.message || "unknown").slice(0, 120),
        });
      } finally {
        this.loading = false;
      }
    },
    // The transcript changed under us — force-refresh and ask the user to retry
    // so they never download a stale version. Throws an isStale error so
    // handleExport stops without recording a completed/failed export.
    async handleStaleConflict(workspaceUUID, audioFileUUID) {
      // Store signature is (audioFileUUID, workspaceUUID, force) — these were
      // previously passed swapped, so "we've refreshed it" never refreshed.
      await this.workspaceStore.fetchAudioFile(
        audioFileUUID,
        workspaceUUID,
        true,
      );
      this.$toast.error(
        "The transcript changed since you opened Export. We've refreshed it — please export again.",
      );
      const stale = new Error("Transcript changed");
      stale.isStale = true;
      throw stale;
    },
    async exportTranscriptFile(workspaceUUID, audioFileUUID, requestBody) {
      const { data, error } = await exportTranscript(
        workspaceUUID,
        audioFileUUID,
        requestBody,
      );

      if (error) {
        if (error.status === 409) {
          await this.handleStaleConflict(workspaceUUID, audioFileUUID);
        }
        throw new Error(error.message || "Export failed.");
      }

      // downloadFile is async: awaited, so a failed save lands in the
      // caller's catch instead of being reported as done.
      await downloadFile(
        data,
        this.exportBaseName(requestBody.filename),
        requestBody.format,
      );
      this.$toast.success(`Exported as ${requestBody.format.toUpperCase()}.`);
    },
    // Bundle 2+ text artifacts into one .zip via the backend archive endpoint.
    async exportArchiveBundle(
      workspaceUUID,
      audioFileUUID,
      { requestBody, includeTranscript, includeSummary, includeChat },
    ) {
      const body = {
        schemaVersion: requestBody.schemaVersion,
        // When the transcript isn't in the zip (e.g. clipboard transcript), send
        // a valid placeholder format — the backend ignores it when include is
        // false, but the DTO still validates the enum.
        format: includeTranscript ? requestBody.format : "txt",
        filename: requestBody.filename,
        transcript: { ...requestBody.transcript, include: includeTranscript },
        includeSummary,
        includeChat,
        expectedTranscriptRevision: requestBody.expectedTranscriptRevision,
      };

      const { data, error } = await exportArchive(
        workspaceUUID,
        audioFileUUID,
        body,
      );

      if (error) {
        if (error.status === 409) {
          await this.handleStaleConflict(workspaceUUID, audioFileUUID);
        }
        throw new Error(error.message || "Export failed.");
      }

      await downloadFile(
        data,
        `${this.exportBaseName(this.filename)}-export`,
        "zip",
      );
      this.$toast.success("Exported as a .zip.");
    },
    // Mirror the backend's buildExportHeaders sanitization so the saved file
    // name (which the browser takes from downloadFile, not Content-Disposition)
    // is consistent and safe across formats.
    exportBaseName(name) {
      const base = (name || "transcript")
        .replace(/\.[^/.]+$/, "") // strip any extension the user typed
        // eslint-disable-next-line no-control-regex -- intentional: strip control chars from filenames
        .replace(/[\x00-\x1F\x7F]/g, "") // remove control chars
        .replace(/[^\w.-]+/g, "_") // unsafe chars (incl spaces) -> underscore
        .replace(/^_+|_+$/g, "") // trim leading/trailing underscores
        .slice(0, 200);
      return base || "transcript";
    },
    /**
     * Clipboard is rendered CLIENT-SIDE (no network round-trip) so the copy
     * happens within the user-activation window — an async backend call first
     * can cause browsers to reject the clipboard write. Output is byte-identical
     * to the backend txt export (pinned by the shared contract fixtures).
     */
    async copyTranscriptToClipboard() {
      const text = renderTranscriptText(this.words, {
        ...this.options.transcript,
      });
      // Throws on failure so handleExport records a failure (not a completed
      // export) and doesn't proceed to media.
      await this.copyToClipboard(text);
      this.$toast.success("Transcript copied to clipboard.");
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch (e) {
        // Fall through to the legacy execCommand path below.
      }
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      let copied = false;
      try {
        copied = document.execCommand("copy");
      } finally {
        document.body.removeChild(textarea);
      }
      if (!copied) {
        throw new Error("Couldn't copy to the clipboard.");
      }
    },
    // Analytics must NEVER affect export control flow — a throw here should not
    // abort a download or turn a success into a false error.
    trackQuietly(method, props) {
      try {
        analytics[method]?.(props);
      } catch (e) {
        /* swallow: telemetry is best-effort */
      }
    },
    // Summary + chat history reuse the existing, proven download endpoints and
    // come down as their own .txt files (best-effort — a missing summary/chat
    // is reported but doesn't fail the rest of the export).
    async exportSummary(workspaceUUID, audioFileUUID) {
      this.trackQuietly("exportSummaryClicked", {
        audio_file_uuid: audioFileUUID,
      });
      try {
        const { data, error } = await downloadSummary(
          workspaceUUID,
          audioFileUUID,
        );
        if (error || !data) throw new Error(error?.message || "No summary");
        await downloadFile(
          data,
          `${this.exportBaseName(this.filename)}-summary`,
          "txt",
        );
        this.$toast.success("Summary downloaded.");
        this.trackQuietly("exportSummaryCompleted", {
          audio_file_uuid: audioFileUUID,
        });
        return true;
      } catch (e) {
        this.$toast.error("Couldn't download the summary for this file.");
        return false;
      }
    },
    async exportChatHistory(workspaceUUID, audioFileUUID) {
      this.trackQuietly("exportChatHistoryClicked", {
        audio_file_uuid: audioFileUUID,
      });
      try {
        const { data, error } = await downloadChatHistory(
          workspaceUUID,
          audioFileUUID,
        );
        if (error || !data) throw new Error(error?.message || "No chat");
        await downloadFile(
          data,
          `${this.exportBaseName(this.filename)}-chat`,
          "txt",
        );
        this.$toast.success("Chat history downloaded.");
        this.trackQuietly("exportChatHistoryCompleted", {
          audio_file_uuid: audioFileUUID,
        });
        return true;
      } catch (e) {
        this.$toast.error("Couldn't download the chat history for this file.");
        return false;
      }
    },
    async exportMedia(workspaceUUID, audioFileUUID) {
      let url;
      try {
        // The API helper resolves HTTP failures as { error } rather than
        // throwing; unchecked, a failure "downloaded" a link to null and
        // reported success.
        const { data, error } =
          (await downloadAudioFile(workspaceUUID, audioFileUUID)) || {};
        if (error || !data) throw new Error(error?.message || "No media URL");
        url = data;
      } catch (e) {
        console.error("Couldn't get a media download URL:", e);
        this.$toast.error("Couldn't get the media file for this recording.");
        return false;
      }
      const link = document.createElement("a");
      link.target = "_blank";
      link.href = url;
      link.download = this.audioFile?.userDefinedName || "media-file";
      document.body.appendChild(link);
      try {
        link.click();
        this.$toast.success("Media file downloaded.");
        return true;
      } catch (e) {
        // A second auto-download may be blocked by the browser; report it but
        // don't fail the whole export.
        this.$toast.error(
          "Couldn't start the media download — your browser may have blocked it.",
        );
        return false;
      } finally {
        link.remove();
      }
    },
  },
};
</script>

<style scoped>
/* The `.popup-wrap` compound bumps specificity over the global
   `.popup-holder .popup-wrap { overflow-y: auto }` so the whole modal no longer
   scrolls — instead it's a fixed-height flex column and only the preview box
   scrolls internally. */
.advanced-export.popup-wrap {
  max-width: 880px;
  width: 92vw;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
}

.advanced-export__body {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 24px;
  flex: 1 1 auto;
  min-height: 0; /* let grid children shrink so the preview can scroll */
  overflow: hidden;
}

.advanced-export__preview {
  min-height: 0;
  overflow: hidden; /* the inner grey box (.export-preview) does the scrolling */
}

.advanced-export__options {
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
}

.ae-section {
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  padding: 14px 16px;
}

.ae-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ae-section__title {
  font-weight: 600;
  font-size: 15px;
}

.ae-section__body {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ae-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
}

.ae-select {
  padding: 6px 10px;
  border: 1px solid #d4d4d8;
  border-radius: 6px;
  min-width: 140px;
}

.ae-more {
  background: none;
  border: none;
  color: #f16622;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  padding: 0;
}

.ae-more__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ae-check {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  cursor: pointer;
}

.ae-check--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.ae-note {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
}

.ae-switch {
  position: relative;
  display: inline-block;
  width: 38px;
  height: 22px;
}

.ae-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.ae-switch__slider {
  position: absolute;
  inset: 0;
  background: #d4d4d8;
  border-radius: 22px;
  transition: 0.2s;
}

.ae-switch__slider::before {
  content: "";
  position: absolute;
  height: 16px;
  width: 16px;
  left: 3px;
  bottom: 3px;
  background: #fff;
  border-radius: 50%;
  transition: 0.2s;
}

.ae-switch input:checked + .ae-switch__slider {
  background: #f16622;
}

.ae-switch input:checked + .ae-switch__slider::before {
  transform: translateX(16px);
}

.advanced-export__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.ae-filename {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  flex: 1;
  min-width: 220px;
}

.ae-filename__input {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #d4d4d8;
  border-radius: 6px;
}

.ae-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* A subtle text link (it is an action, not navigation — so a real <button>). Sits to
   the left of Cancel/Export and clears the user's saved export preferences. */
.ae-reset {
  background: none;
  border: none;
  padding: 0;
  color: #9ca3af;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.ae-reset:hover:not(:disabled) {
  color: #6b7280;
}

.ae-reset:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #fff;
  border-radius: 50%;
  width: 14px;
  height: 14px;
  animation: spin 1s linear infinite;
  display: inline-block;
  vertical-align: middle;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ae-switch__slider,
  .ae-switch__slider::before,
  .spinner {
    transition: none;
    animation: none;
  }
}

@media (max-width: 720px) {
  /* On narrow screens stack the columns and let the whole body scroll, giving
     the preview a usable fixed height instead of fighting for flex space. */
  .advanced-export__body {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }

  .advanced-export__preview {
    height: 40vh;
  }
}
</style>
